import React, { useMemo, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Stepper } from '@lomadee/design-system';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import SignUpMutation, {
  SignUpMutationVariablesI,
} from '@plug/domains/auth/graphql/mutations/signup-mutation';
import SIGN_IN_MUTATION from '@plug/domains/auth/graphql/mutations/signin-mutation';
import { useCreateAccountContext } from '@plug/contexts/CreateAccountContext';
import AffiliateRegistrationPasswordStep from './components/AffiliateRegistrationPasswordStep';
import CreateAccountContainer from '../../../../components/create-account-container';
import AffiliateRegistrationPersonalDataStep from './components/AffiliateRegistrationPersonalDataStep';
import AffiliateRegistrationBankInformationStep from './components/AffiliateRegistrationBankInformationStep';
import { MiniStepperWrapper, StepperWrapper } from './style';
import { useLogin } from '@plug/domains/auth/hooks/useLogin';
import {
  GetCustomizationResponse,
  GET_CUSTOMIZATION,
} from '@plug/domains/organization/domains/affiliate/graphql/mutations/get-customizations';
import {
  getInstalledAffiliateStoreAppByDomainQuery,
  getInstalledAffiliateStoreAppByDomainInput,
  GET_INSTALLED_AFFILIATE_STORE_APP_BY_DOMAIN,
} from '@plug/domains/organization/domains/affiliate/graphql/queries/get-installed-affiliate-store-app-by-domain';
import { getHublyFormUnmaskedValue, parseHublyFormFieldsToForm } from '@plug/helpers/hubly-form';
import { useTranslation } from 'react-i18next';
import { DOMAIN, I18N_NAMESPACES, MARY_KAY_WHITELABEL } from '@plug/config/consts';

interface Props {
  email?: string;
  phone?: string;
  name?: string;
  organizationName?: string;
  incompleteFlow?: boolean;
}

const AffiliateSignUp = ({
  email,
  phone = '',
  name = '',
  organizationName,
  incompleteFlow,
}: Props): JSX.Element => {
  const history = useHistory();
  const [signUp, { loading }] = useMutation<any, SignUpMutationVariablesI>(SignUpMutation);
  const [signIn] = useMutation(SIGN_IN_MUTATION);
  const { login } = useLogin();
  const { t } = useTranslation();
  const {
    step,
    addValues,
    values,
    incompleteRegistration,
    setIncompleteRegistration,
    whitelabelCustomization,
    role,
    signupType,
    setSignupType,
    setIsCreatingAccount,
    indicatedBy,
    organizationId,
    direct,
  } = useCreateAccountContext();

  useQuery<GetCustomizationResponse>(GET_CUSTOMIZATION, {
    onCompleted: data => {
      if (data?.getCustomization) {
        whitelabelCustomization.setValues(data.getCustomization);
      }
    },
    skip: !!whitelabelCustomization?.values || role === 'ADMIN',
  });

  useQuery<getInstalledAffiliateStoreAppByDomainQuery, getInstalledAffiliateStoreAppByDomainInput>(
    GET_INSTALLED_AFFILIATE_STORE_APP_BY_DOMAIN,
    {
      variables: {
        input: {
          id: '60d2193024d3230e2bdd7a5f',
        },
      },
      onCompleted: data => {
        if (
          data?.getInstalledAffiliateStoreAppByDomain?.requirements?.length &&
          !whitelabelCustomization.requirements.length
        ) {
          whitelabelCustomization.setRequirements(
            data.getInstalledAffiliateStoreAppByDomain.requirements,
          );
        }
      },
      skip:
        whitelabelCustomization.requirements.length > 0 ||
        !whitelabelCustomization.values ||
        role === 'ADMIN',
    },
  );

  useEffect(() => {
    if (incompleteFlow && !incompleteRegistration) {
      setIncompleteRegistration(incompleteFlow);
    }
  }, [incompleteFlow, incompleteRegistration, setIncompleteRegistration]);

  useEffect(() => {
    if (email && signupType !== 'invite') {
      setSignupType('invite');
    }
  }, [email, setSignupType, signupType]);

  useEffect(() => {
    if (email && !values.email) {
      addValues({
        email,
      });
    }
  }, [email, values.email, addValues]);

  const handleAffiliateCreateAccountSucessfull = useCallback(() => {
    toast.success(
      'Cadastro finalizado com sucesso! Agora, vamos te redirecionar para a plataforma.',
    );

    signIn({
      variables: {
        input: {
          email: values.email.toLowerCase(),
          password: values.password,
        },
      },
    })
      .then(({ data }) => {
        login({
          id: data.signIn.user.id,
          token: data.signIn.token,
          email: data.signIn.user.email,
          username: data.signIn.user.username,
          cookieDays: 15,
          loginAnimation: true,
          redirect: true,
        });
      })
      .catch(error => {
        history.push('/login');
      });
  }, [signIn, values.email, values.password, login, history]);

  const handleAffiliateCreateAccount = useCallback(
    (values: CreateAccountFormI) => {
      const documentOnlyNumbers = values.document.replace(/\D/g, '');
      const phoneOnlyNumbers = values.phone.replace(/\D/g, '');

      setIsCreatingAccount(true);

      return signUp({
        variables: {
          input: {
            incompleteFlow: incompleteRegistration,
            document: documentOnlyNumbers,
            documentType: values.document.length > 12 ? 'cnpj' : 'cpf',
            email: values.email.toLowerCase(),
            password: values.password,
            username: values.name,
            phone: phoneOnlyNumbers,
            direct,
            ...(values.customFields && {
              customFields: parseHublyFormFieldsToForm<{ key: string; value: string }>({
                formData: values.customFields,
                requirements: whitelabelCustomization.requirements as any,
                keyLabel: 'key',
                onParse(requirement: any, { key, value }) {
                  if ((requirement.additionalFields as Record<string, any>)?.type === 'date') {
                    return { key, value };
                  }

                  return {
                    key,
                    value: requirement.additionalFields?.mask
                      ? getHublyFormUnmaskedValue(value)
                      : value,
                  };
                },
              }),
            }),
            ...(values.bank && {
              account: values.account,
              agencyDigit: values.agencyDigit,
              accountDigit: values.accountDigit,
              accountHolderName: values.accountHolderName,
              agency: values.agency,
              bank: values.bank,
              bankType: values.bankType,
            }),
            indicatedBy,
            organizationId,
          },
        },
      })
        .then(() => {
          handleAffiliateCreateAccountSucessfull();
          setIncompleteRegistration(false);
        })
        .catch(error => {
          setIsCreatingAccount(false);
          const err = error?.graphQLErrors[0]?.message;

          if (err === 'user already registered.') {
            return toast.error('Já existe uma conta utilizando este e-mail.');
          }

          if (err.includes('The bank is not available at Cip at the moment')) {
            return toast.error(
              t(`errors.${err}`, {
                ns:
                  I18N_NAMESPACES[MARY_KAY_WHITELABEL.includes(DOMAIN) ? 'DOMAIN' : 'TRANSLATION'],
              }),
            );
          }

          if (!err.includes('_')) {
            return toast.error(err);
          }

          return toast.error(
            'Erro ao criar sua conta. Por favor, tente novamente em alguns instantes.',
          );
        });
    },
    [
      direct,
      handleAffiliateCreateAccountSucessfull,
      incompleteRegistration,
      indicatedBy,
      organizationId,
      setIncompleteRegistration,
      setIsCreatingAccount,
      signUp,
      t,
      whitelabelCustomization.requirements,
    ],
  );

  const Component = useMemo(() => {
    switch (step) {
      case 'PASSWORD':
        return <AffiliateRegistrationPasswordStep />;
      case 'PERSONAL_DATA':
        return <AffiliateRegistrationPersonalDataStep />;
      case 'BANK_INFORMATION':
        return (
          <AffiliateRegistrationBankInformationStep
            handleCreateAccount={handleAffiliateCreateAccount}
          />
        );
      default:
        return <h1>default</h1>;
    }
  }, [handleAffiliateCreateAccount, step]);

  const StepNumber = (function(): number {
    switch (step) {
      case 'PASSWORD':
        return 0;
      case 'PERSONAL_DATA':
        return 1;
      case 'BANK_INFORMATION':
        return 2;
    }
  })();

  return (
    <CreateAccountContainer
      doingAnimation={false}
      organizationName={organizationName}
      type={'affiliate'}
    >
      <StepperWrapper>
        <Stepper hierarchy="ghost" steps={3} currentStep={StepNumber} />
      </StepperWrapper>

      {Component}

      <MiniStepperWrapper>
        <Stepper steps={3} currentStep={StepNumber} mini />
      </MiniStepperWrapper>
    </CreateAccountContainer>
  );
};

export default AffiliateSignUp;
